import { toast } from "react-toastify";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { X } from "lucide-react";
import axios from "axios";

import { checkStaffInvite, createUser, resendInvitationById } from "../config/url";
import { getConfigWithToken } from "../config/config";
import { STAFF_ROLES } from "../utils/staffRoles";
import classes from "./InviteStaffDrawer.module.css";

const INVITE_ROLES = [
	{
		badgeClass: classes.badgeAdmin,
		description: "Full system access.",
		key: "Admin",
	},
	{
		badgeClass: classes.badgeProvider,
		description: "Delivers services. Has a caseload and appears in visit records.",
		key: "Provider",
	},
	{
		badgeClass: classes.badgeSupervisor,
		description: "Oversees providers. Can review and approve records.",
		key: "Supervisor",
	},
	{
		badgeClass: classes.badgeQa,
		description: "Quality assurance access - review records and flag issues.",
		key: "QA",
	},
].filter((role) => STAFF_ROLES.includes(role.key));

const emptyForm = { email: "", firstName: "", lastName: "", npi: "", role: "", userId: "" };
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMAIL_CHECK_DEBOUNCE_MS = 450;

const getUserIdLabel = (role) => {
	if (role === "Admin") return "Admin ID";
	if (role === "Provider") return "Provider ID";
	if (role === "QA") return "QA ID";
	if (role === "Supervisor") return "Supervisor ID";
	return "User ID";
};

const InviteStaffDrawer = ({ isOpen, onClose, onSuccess, onViewProfile }) => {
	const currentUser = useSelector((state) => state?.user);
	const token = currentUser?.user?.token || localStorage.getItem("user_id");
	const organizationId =
		currentUser?.user?.organizationId || localStorage.getItem("organizationId") || localStorage.getItem("businessId");

	const [errors, setErrors] = useState({});
	const [form, setForm] = useState(emptyForm);
	const [inviteCheck, setInviteCheck] = useState(null);
	const [isCheckingEmail, setIsCheckingEmail] = useState(false);
	const [isResending, setIsResending] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const emailCheckTimerRef = useRef(null);

	const hasProviderRole = form.role === "Provider";
	const userIdLabel = getUserIdLabel(form.role);
	const isExistingMembership = inviteCheck?.code === "EXISTING_MEMBERSHIP";
	const isExistingUser = inviteCheck?.code === "EXISTING_USER";
	const isAdminEmail = inviteCheck?.code === "ADMIN_EMAIL";
	const canSubmit = !isExistingMembership && !isAdminEmail;

	const requestHeaders = useMemo(
		() => ({
			Accept: "application/json",
			"Content-Type": "application/json",
			...(token ? getConfigWithToken(token).headers : {}),
			...(organizationId ? { "x-organization-id": organizationId } : {}),
		}),
		[organizationId, token],
	);

	const resetInviteCheck = () => {
		setInviteCheck(null);
		setIsCheckingEmail(false);
		if (emailCheckTimerRef.current) {
			clearTimeout(emailCheckTimerRef.current);
			emailCheckTimerRef.current = null;
		}
	};

	const runEmailCheck = async (emailValue) => {
		const normalizedEmail = emailValue.trim().toLowerCase();
		if (!normalizedEmail || !EMAIL_PATTERN.test(normalizedEmail)) {
			resetInviteCheck();
			return;
		}

		if (!organizationId) {
			setInviteCheck({
				code: "MISSING_ORG",
				message: "Organization context is missing. Please sign in again.",
			});
			return;
		}

		setIsCheckingEmail(true);
		try {
			const response = await axios.get(checkStaffInvite, {
				headers: requestHeaders,
				params: { email: normalizedEmail },
			});
			const result = {
				code: response.data?.code || null,
				data: response.data?.data || null,
				message: response.data?.message || "",
			};
			setInviteCheck(result);

			if (result.code === "EXISTING_USER" || result.code === "EXISTING_MEMBERSHIP") {
				setForm((previous) => ({
					...previous,
					email: normalizedEmail,
					firstName: result.data?.firstname || previous.firstName,
					lastName: result.data?.lastname || previous.lastName,
					userId: result.data?.staffUserId || previous.userId,
				}));
			}
		} catch (error) {
			setInviteCheck({
				code: "CHECK_FAILED",
				message: error.response?.data?.message || "Unable to verify this email address.",
			});
		} finally {
			setIsCheckingEmail(false);
		}
	};

	useEffect(() => {
		if (!isOpen) return undefined;
		setForm(emptyForm);
		setErrors({});
		resetInviteCheck();
		const onKeyDown = (event) => {
			if (event.key === "Escape") onClose();
		};
		document.addEventListener("keydown", onKeyDown);
		document.body.style.overflow = "hidden";
		return () => {
			document.removeEventListener("keydown", onKeyDown);
			document.body.style.overflow = "";
			resetInviteCheck();
		};
	}, [isOpen, onClose]);

	useEffect(() => {
		if (!isOpen) return undefined;

		if (emailCheckTimerRef.current) clearTimeout(emailCheckTimerRef.current);

		const normalizedEmail = form.email.trim().toLowerCase();
		if (!normalizedEmail || !EMAIL_PATTERN.test(normalizedEmail)) {
			resetInviteCheck();
			return undefined;
		}

		emailCheckTimerRef.current = setTimeout(() => {
			runEmailCheck(normalizedEmail);
		}, EMAIL_CHECK_DEBOUNCE_MS);

		return () => {
			if (emailCheckTimerRef.current) clearTimeout(emailCheckTimerRef.current);
		};
	}, [form.email, isOpen, organizationId, requestHeaders]);

	if (!isOpen) return null;

	const updateField = (field, value) => {
		setForm((previous) => ({ ...previous, [field]: value }));
		setErrors((previous) => {
			const next = { ...previous };
			delete next[field];
			return next;
		});
		if (field === "email") resetInviteCheck();
	};

	const selectRole = (roleKey) => {
		setForm((previous) => ({
			...previous,
			npi: roleKey === "Provider" ? previous.npi : "",
			role: roleKey,
		}));
		setErrors((previous) => {
			const next = { ...previous };
			delete next.npi;
			delete next.role;
			if (next.userId) next.userId = `${getUserIdLabel(roleKey)} is required.`;
			return next;
		});
	};

	const validate = () => {
		const nextErrors = {};
		if (!form.firstName.trim()) nextErrors.firstName = "First name is required.";
		if (!form.lastName.trim()) nextErrors.lastName = "Last name is required.";
		if (!form.email.trim()) nextErrors.email = "Email is required.";
		if (!form.userId.trim() && !isExistingUser && !isExistingMembership) {
			nextErrors.userId = `${userIdLabel} is required.`;
		}
		if (!form.role) nextErrors.role = "Select a role.";
		if (form.role === "Provider" && !form.npi.trim()) {
			nextErrors.npi = "NPI number is required for Provider access.";
		}
		if (!organizationId) nextErrors.email = "Organization context is missing. Please sign in again.";
		setErrors(nextErrors);
		return Object.keys(nextErrors).length === 0;
	};

	const handleSubmit = async (event) => {
		event.preventDefault();
		if (!canSubmit || !validate()) return;

		setIsSubmitting(true);
		try {
			const payload = {
				email: form.email.trim(),
				firstname: form.firstName.trim(),
				isactive: true,
				lastname: form.lastName.trim(),
				npi: hasProviderRole ? form.npi.trim() : null,
				organization_id: organizationId,
				role: form.role,
				userId: form.userId.trim() || inviteCheck?.data?.staffUserId || "",
			};
			const response = await axios.post(createUser, payload, { headers: requestHeaders });
			if (response.data?.success === false || response.data?.success === "false") {
				if (response.data?.code === "EXISTING_MEMBERSHIP") {
					setInviteCheck({
						code: "EXISTING_MEMBERSHIP",
						data: response.data?.data || null,
						message: response.data?.message || "This person is already a member of your organization.",
					});
					return;
				}
				toast.error(response.data?.message || "Failed to send invitation.");
				return;
			}
			toast.success(response.data?.message || "Invitation sent successfully.");
			onSuccess?.();
			onClose();
		} catch (error) {
			const responseData = error.response?.data;
			if (responseData?.code === "EXISTING_MEMBERSHIP") {
				setInviteCheck({
					code: "EXISTING_MEMBERSHIP",
					data: responseData?.data || null,
					message: responseData?.message || "This person is already a member of your organization.",
				});
				return;
			}
			toast.error(responseData?.message || "Failed to send invitation.");
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleResendInvitation = async () => {
		const userId = inviteCheck?.data?.userId;
		if (!userId) return;

		setIsResending(true);
		try {
			const response = await axios.post(resendInvitationById(userId), {}, { headers: requestHeaders });
			if (response.data?.success === false) {
				toast.error(response.data?.message || "Failed to resend invitation.");
				return;
			}
			toast.success(response.data?.message || "Invitation resent.");
		} catch (error) {
			toast.error(error.response?.data?.message || "Failed to resend invitation.");
		} finally {
			setIsResending(false);
		}
	};

	const handleViewProfile = () => {
		const userId = inviteCheck?.data?.userId;
		if (!userId) return;
		onViewProfile?.(userId);
		onClose();
	};

	const renderInviteNotice = () => {
		if (isCheckingEmail) {
			return (
				<div className={`${classes.notice} ${classes.noticeInfo}`}>
					<p className={classes.noticeText}>Checking whether this email already has a TeleTrace account...</p>
				</div>
			);
		}

		if (!inviteCheck?.code) return null;

		if (inviteCheck.code === "NEW_USER") {
			return (
				<div className={`${classes.notice} ${classes.noticeInfo}`}>
					<p className={classes.noticeText}>
						No TeleTrace account found. A new account and organization membership will be created.
					</p>
				</div>
			);
		}

		if (inviteCheck.code === "EXISTING_USER") {
			return (
				<div className={`${classes.notice} ${classes.noticeInfo}`}>
					<p className={classes.noticeTitle}>Existing TeleTrace account</p>
					<p className={classes.noticeText}>{inviteCheck.message}</p>
				</div>
			);
		}

		if (inviteCheck.code === "EXISTING_MEMBERSHIP") {
			const status = inviteCheck.data?.status || "Active";
			return (
				<div className={`${classes.notice} ${classes.noticeWarning}`}>
					<p className={classes.noticeTitle}>Already a member</p>
					<p className={classes.noticeText}>{inviteCheck.message}</p>
					<p className={classes.noticeMeta}>
						Status: <strong>{status}</strong>
						{inviteCheck.data?.role ? (
							<>
								{" "}
								· Role: <strong>{inviteCheck.data.role}</strong>
							</>
						) : null}
					</p>
					<div className={classes.noticeActions}>
						<button className={classes.noticeButton} onClick={handleViewProfile} type="button">
							View Staff Profile
						</button>
						{status === "Pending" ? (
							<button
								className={classes.noticeButtonPrimary}
								disabled={isResending}
								onClick={handleResendInvitation}
								type="button">
								{isResending ? "Resending..." : "Resend Invitation"}
							</button>
						) : null}
					</div>
				</div>
			);
		}

		if (inviteCheck.code === "ADMIN_EMAIL" || inviteCheck.code === "MISSING_ORG" || inviteCheck.code === "CHECK_FAILED") {
			return (
				<div className={`${classes.notice} ${classes.noticeError}`}>
					<p className={classes.noticeText}>{inviteCheck.message}</p>
				</div>
			);
		}

		return null;
	};

	return (
		<>
			<button aria-label="Close invite drawer" className={classes.backdrop} onClick={onClose} type="button" />
			<aside aria-labelledby="invite-staff-title" aria-modal="true" className={classes.drawer} role="dialog">
				<div className={classes.header}>
					<div className={classes.headerTop}>
						<h2 className={classes.title} id="invite-staff-title">
							Invite Staff Member
						</h2>
						<button aria-label="Close" className={classes.closeButton} onClick={onClose} type="button">
							<X size={18} />
						</button>
					</div>
					<p className={classes.subtitle}>They&apos;ll receive an email to set up their account.</p>
				</div>

				<form className={classes.form} onSubmit={handleSubmit}>
					<div className={classes.body}>
						<div className={classes.nameRow}>
							<label className={classes.field}>
								<span className={classes.label}>
									First Name <span className={classes.required}>*</span>
								</span>
								<input
									className={`${classes.input} ${errors.firstName ? classes.inputError : ""}`}
									disabled={isExistingMembership}
									onChange={(event) => updateField("firstName", event.target.value)}
									type="text"
									value={form.firstName}
								/>
								{errors.firstName ? <p className={classes.errorText}>{errors.firstName}</p> : null}
							</label>

							<label className={classes.field}>
								<span className={classes.label}>
									Last Name <span className={classes.required}>*</span>
								</span>
								<input
									className={`${classes.input} ${errors.lastName ? classes.inputError : ""}`}
									disabled={isExistingMembership}
									onChange={(event) => updateField("lastName", event.target.value)}
									type="text"
									value={form.lastName}
								/>
								{errors.lastName ? <p className={classes.errorText}>{errors.lastName}</p> : null}
							</label>
						</div>

						<label className={classes.field}>
							<span className={classes.label}>
								Email <span className={classes.required}>*</span>
							</span>
							<input
								className={`${classes.input} ${errors.email ? classes.inputError : ""}`}
								onChange={(event) => updateField("email", event.target.value)}
								type="email"
								value={form.email}
							/>
							{errors.email ? <p className={classes.errorText}>{errors.email}</p> : null}
						</label>

						{renderInviteNotice()}

						<label className={classes.field}>
							<span className={classes.label}>
								{userIdLabel}{" "}
								{!isExistingUser && !isExistingMembership ? <span className={classes.required}>*</span> : null}
							</span>
							<input
								className={`${classes.input} ${errors.userId ? classes.inputError : ""}`}
								disabled={isExistingUser || isExistingMembership}
								onChange={(event) => updateField("userId", event.target.value)}
								type="text"
								value={form.userId}
							/>
							{errors.userId ? <p className={classes.errorText}>{errors.userId}</p> : null}
						</label>

						<div className={classes.field}>
							<span className={classes.label}>
								Role <span className={classes.required}>*</span>
							</span>
							<div className={classes.roleList}>
								{INVITE_ROLES.map((role) => {
									const isSelected = form.role === role.key;
									return (
										<label
											className={`${classes.roleOption} ${isSelected ? classes.roleOptionSelected : ""}`}
											key={role.key}>
											<input
												checked={isSelected}
												className={classes.checkbox}
												disabled={isExistingMembership}
												name="staff-role"
												onChange={() => selectRole(role.key)}
												type="radio"
											/>
											<span className={classes.roleContent}>
												<span className={`${classes.roleBadge} ${role.badgeClass}`}>{role.key}</span>
												<span className={classes.roleDescription}>{role.description}</span>
											</span>
										</label>
									);
								})}
							</div>
							{errors.role ? <p className={classes.errorText}>{errors.role}</p> : null}
						</div>

						{hasProviderRole ? (
							<div className={classes.providerSection}>
								<p className={classes.providerHeading}>Provider details (if Provider role selected)</p>
								<label className={classes.field}>
									<span className={classes.label}>
										NPI Number <span className={classes.required}>*</span>
									</span>
									<input
										className={`${classes.input} ${errors.npi ? classes.inputError : ""}`}
										disabled={isExistingMembership}
										inputMode="numeric"
										onChange={(event) =>
											updateField("npi", event.target.value.replace(/\D/g, "").slice(0, 10))
										}
										type="text"
										value={form.npi}
									/>
									{errors.npi ? <p className={classes.errorText}>{errors.npi}</p> : null}
								</label>
							</div>
						) : null}
					</div>

					<div className={classes.footer}>
						<button className={classes.cancelButton} onClick={onClose} type="button">
							Cancel
						</button>
						<button className={classes.submitButton} disabled={isSubmitting || !canSubmit} type="submit">
							{isSubmitting ? "Sending..." : "Send Invitation"}
						</button>
					</div>
				</form>
			</aside>
		</>
	);
};

export default InviteStaffDrawer;
